/**
 * Menú de usuario (avatar, arriba a la derecha): Sugerencias (buzón) + cerrar sesión.
 * El idioma ES | EN vive en el encabezado (LangToggle), no aquí.
 * Vive FUERA del contenedor con scroll del Header (overflow-x != visible recortaría el desplegable).
 */
import { useState } from 'react';
import { LogOut, MessageSquare } from 'lucide-react';
import FeedbackModal from '../FeedbackModal';
import { useLang } from '../../context/LangContext';
import { useUserContext } from '../../context/UserContext';
import { signOutWithAudit } from '../../utils/signOutWithAudit';
import { t } from '../../i18n/dictionary';

function getInitials(nameOrEmail: string): string {
  const trimmed = nameOrEmail.trim();
  if (!trimmed) return '';
  if (trimmed.includes('@')) return (trimmed.split('@')[0]?.[0] ?? '').toUpperCase();
  const parts = trimmed.split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

export default function UserMenu() {
  const { lang } = useLang();
  const { activeUser } = useUserContext();
  const [open, setOpen] = useState(false);

  // Feedback modal (buzón de sugerencias) — feature aislada, sin relación
  // con journal_entries/validateEntry()/Triple Write/AccountingEngine.ts.
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // Discoverability: un punto hasta el primer uso (metadata de UI en localStorage, no dato contable).
  const [feedbackSeen, setFeedbackSeen] = useState(() => {
    try {
      return localStorage.getItem('quipus_feedback_seen') === 'true';
    } catch {
      return true; // fail-safe: si localStorage no está disponible, no insistir con el punto
    }
  });

  const handleLogout = async () => {
    await signOutWithAudit('manual');
    window.location.href = '/';
  };

  const openFeedback = () => {
    setOpen(false);
    setFeedbackOpen(true);
    if (!feedbackSeen) {
      try { localStorage.setItem('quipus_feedback_seen', 'true'); } catch { /* ignore */ }
      setFeedbackSeen(true);
    }
  };

  return (
    <div className="relative flex-shrink-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="hd-avatar fx relative"
        aria-label={t('header.userMenu', lang)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={activeUser.displayName}
      >
        {getInitials(activeUser.displayName) || '?'}
        {!feedbackSeen && (
          <span
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full"
            style={{ background: 'var(--negative)', border: '2px solid var(--surface)' }}
          />
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden="true" />
          <div role="menu" className="menu-pop absolute top-full right-0 mt-2 w-64 z-50 py-1.5">
            <div className="px-3 py-2" style={{ borderBottom: '1px solid var(--border)' }}>
              <p className="truncate" style={{ fontSize: 'var(--type-body-size)', fontWeight: 600, color: 'var(--ink)' }}>
                {activeUser.displayName}
              </p>
            </div>

            <button role="menuitem" onClick={openFeedback} className="menu-row">
              <MessageSquare size={16} aria-hidden="true" />
              {t('header.feedbackMenu', lang)}
            </button>

            <div style={{ borderTop: '1px solid var(--border)' }}>
              <button role="menuitem" onClick={handleLogout} className="menu-row">
                <LogOut size={16} aria-hidden="true" />
                {t('header.logout', lang)}
              </button>
            </div>
          </div>
        </>
      )}

      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
    </div>
  );
}
